import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AdminHeader from "../../component/AdminHeader";
import CustomImageUpload from "../../component/CustomImageUpload";
import CustomSelect from "../../component/CustomSelect";
import DynamicCKEditor from "../../component/DynamicCkEditor";
import ApiRequest from "../../helper/ApiRequest";
import { ApiEndpoints } from "../../helper/ApiEndpoints";
import { BaseUrl } from "../../helper/BaseUrl";

const emptyTable = {
  columns: ["Feature", "Mobility Infotech Logistics", "Competitor"],
  rows: [],
};

const emptyTableRow = {
  feature: "",
  values: [
    { type: "Yes", content: "" },
    { type: "Yes", content: "" },
  ],
};

const createEmptyTable = () => ({
  columns: [...emptyTable.columns],
  rows: [{ feature: emptyTableRow.feature, values: emptyTableRow.values.map((value) => ({ ...value })) }],
});

const createEmptySection = () => ({
  heading: "",
  description: "",
  comparison_table_enabled: false,
  comparison_table: createEmptyTable(),
});

const normalizeValue = (value) => {
  if (value && typeof value === "object" && value.type) {
    return {
      type: value.type,
      content: value.content || "",
    };
  }

  const normalizedValue = String(value || "").trim();
  if (normalizedValue === "Yes" || normalizedValue === "No") {
    return { type: normalizedValue, content: "" };
  }

  return { type: normalizedValue ? "Content" : "Yes", content: normalizedValue };
};

const normalizeRow = (row = []) => {
  if (Array.isArray(row)) {
    return {
      feature: row[0] || "",
      values: [normalizeValue(row[1]), normalizeValue(row[2])],
    };
  }

  return {
    feature: row.feature || "",
    values: [
      normalizeValue(row.values?.[0] || row.mobility || row.value1),
      normalizeValue(row.values?.[1] || row.competitor || row.value2),
    ],
  };
};

const normalizeTable = (table = {}) => {
  const defaultTable = createEmptyTable();
  const columns = Array.isArray(table.columns) && table.columns.length
    ? [...table.columns, ...defaultTable.columns].slice(0, 3)
    : defaultTable.columns;

  return {
    columns,
    rows: Array.isArray(table.rows) && table.rows.length ? table.rows.map(normalizeRow) : defaultTable.rows,
  };
};

const parseTable = (table = {}) => ({
  columns: (table.columns || []).map((item) => String(item || "").trim()).filter(Boolean),
  rows: (table.rows || [])
    .map((row) => ({
      feature: row.feature || "",
      values: (row.values || []).map((value) => ({
        type: value.type || "Yes",
        content: value.type === "Content" ? value.content || "" : "",
      })),
    }))
    .filter((row) => row.feature || row.values.some((value) => value.content || value.type !== "Yes")),
});

const hasEditorContent = (value = "") =>
  String(value)
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, "")
    .trim().length > 0;

const normalizeSection = (section = {}, fallbackTable) => ({
  heading: section.heading || "",
  description: section.description || "",
  comparison_table_enabled: Boolean(section.comparison_table_enabled || section.comparison_table),
  comparison_table: normalizeTable(section.comparison_table || fallbackTable || emptyTable),
});

export default function ComparisonForm({ comparisonId }) {
  const navigate = useNavigate();
  const isEdit = Boolean(comparisonId);

  const [authors, setAuthors] = useState([]);
  const [previews, setPreviews] = useState({
    listing_image: null,
    hero_image: null,
  });

  const [postData, setPostData] = useState({
    title: "",
    slug: "",
    listing_description: "",
    short_description: "",
    author_id: "",
    date: new Date().toISOString().split("T")[0],
    status: "0",
    listing_image: null,
    hero_image: null,
  });

  const [sections, setSections] = useState([createEmptySection(), createEmptySection()]);

  const pagename = useMemo(
    () => ({
      name: "Comparison page",
      user: "Admin",
      innerpage: `Comparison page / ${isEdit ? "Edit" : "Add"} Comparison`,
    }),
    [isEdit]
  );

  useEffect(() => {
    const fetchAuthors = async () => {
      try {
        const response = await ApiRequest("GET", ApiEndpoints.autor.authorList);
        setAuthors(response?.authors || []);
      } catch (error) {
        console.error("Failed to fetch authors:", error);
        setAuthors([]);
      }
    };

    fetchAuthors();
  }, []);

  useEffect(() => {
    if (!isEdit) return;

    const fetchData = async () => {
      try {
        const response = await ApiRequest("GET", `${ApiEndpoints.comparison.Edit}${comparisonId}`);
        const comparison = response.comparison || {};
        const legacyTables = comparison.comparison_tables || [];
        const normalizedSections = comparison.sections?.length
          ? comparison.sections.map((section, index) => normalizeSection(section, legacyTables[index]))
          : [createEmptySection()];

        setPostData((prev) => ({
          ...prev,
          title: comparison.title || comparison.listing_title || "",
          slug: comparison.slug || "",
          listing_description: comparison.listing_description || "",
          short_description: comparison.short_description || "",
          author_id: comparison.author_id || "",
          date: comparison.date ? new Date(comparison.date).toISOString().split("T")[0] : "",
          status: comparison.status || "0",
          listing_image: null,
          hero_image: null,
        }));

        setSections(normalizedSections);
        setPreviews({
          listing_image: comparison.listing_image ? BaseUrl + comparison.listing_image : null,
          hero_image: comparison.hero_image ? BaseUrl + comparison.hero_image : null,
        });
      } catch (error) {
        console.error("Error fetching comparison page:", error);
      }
    };

    fetchData();
  }, [comparisonId, isEdit]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files) {
      const file = files[0];
      setPreviews((prev) => ({ ...prev, [name]: URL.createObjectURL(file) }));
      setPostData((prev) => ({ ...prev, [name]: file }));
      return;
    }

    setPostData((prev) => ({ ...prev, [name]: value }));
  };

  const updateSection = (index, key, value) => {
    setSections((prev) =>
      prev.map((section, sectionIndex) =>
        sectionIndex === index ? { ...section, [key]: value } : section
      )
    );
  };

  const updateSectionTableColumn = (sectionIndex, columnIndex, value) => {
    setSections((prev) =>
      prev.map((section, index) => {
        if (index !== sectionIndex) return section;
        const comparisonTable = normalizeTable(section.comparison_table);
        const columns = [...comparisonTable.columns];
        columns[columnIndex] = value;
        return { ...section, comparison_table: { ...comparisonTable, columns } };
      })
    );
  };

  const updateSectionTableRow = (sectionIndex, rowIndex, key, value) => {
    setSections((prev) =>
      prev.map((section, index) => {
        if (index !== sectionIndex) return section;
        const comparisonTable = normalizeTable(section.comparison_table);
        const rows = comparisonTable.rows.map((row, currentRowIndex) =>
          currentRowIndex === rowIndex ? { ...row, [key]: value } : row
        );
        return { ...section, comparison_table: { ...comparisonTable, rows } };
      })
    );
  };

  const updateSectionTableRowValue = (sectionIndex, rowIndex, valueIndex, key, value) => {
    setSections((prev) =>
      prev.map((section, index) => {
        if (index !== sectionIndex) return section;
        const comparisonTable = normalizeTable(section.comparison_table);
        const rows = comparisonTable.rows.map((row, currentRowIndex) => {
          if (currentRowIndex !== rowIndex) return row;
          const values = row.values.map((rowValue, currentValueIndex) => {
            if (currentValueIndex !== valueIndex) return rowValue;
            return {
              ...rowValue,
              [key]: value,
              ...(key === "type" && value !== "Content" ? { content: "" } : {}),
            };
          });
          return { ...row, values };
        });
        return { ...section, comparison_table: { ...comparisonTable, rows } };
      })
    );
  };

  const addSectionTableRow = (sectionIndex) => {
    setSections((prev) =>
      prev.map((section, index) => {
        if (index !== sectionIndex) return section;
        const comparisonTable = normalizeTable(section.comparison_table);
        return {
          ...section,
          comparison_table: {
            ...comparisonTable,
            rows: [
              ...comparisonTable.rows,
              { feature: emptyTableRow.feature, values: emptyTableRow.values.map((value) => ({ ...value })) },
            ],
          },
        };
      })
    );
  };

  const removeSectionTableRow = (sectionIndex, rowIndex) => {
    setSections((prev) =>
      prev.map((section, index) => {
        if (index !== sectionIndex) return section;
        const comparisonTable = normalizeTable(section.comparison_table);
        const rows = comparisonTable.rows.filter((_, currentRowIndex) => currentRowIndex !== rowIndex);
        return { ...section, comparison_table: { ...comparisonTable, rows: rows.length ? rows : createEmptyTable().rows } };
      })
    );
  };

  const serializeSections = () =>
    sections
      .map((section) => {
        const comparisonTable = parseTable(section.comparison_table);
        return {
          heading: section.heading || "",
          description: section.description || "",
          comparison_table_enabled: Boolean(section.comparison_table_enabled),
          comparison_table: section.comparison_table_enabled ? comparisonTable : null,
        };
      })
      .filter((section) => section.heading || section.description || section.comparison_table_enabled);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!postData.title || !postData.slug) {
      toast.error("Title and slug are mandatory.");
      return;
    }
    if (!postData.author_id) {
      toast.error("Author is mandatory.");
      return;
    }
    if (!postData.listing_description.trim()) {
      toast.error("Listing Description is mandatory.");
      return;
    }
    if (!postData.short_description.trim()) {
      toast.error("Detail Short Description is mandatory.");
      return;
    }
    if (!sections[0]?.heading?.trim()) {
      toast.error("Section 1 is mandatory.");
      return;
    }
    if (!hasEditorContent(sections[0]?.description)) {
      toast.error("Section Description is mandatory.");
      return;
    }
    if (!postData.listing_image && !previews.listing_image) {
      toast.error("Listing Image is mandatory.");
      return;
    }
    if (!postData.hero_image && !previews.hero_image) {
      toast.error("Banner Image is mandatory.");
      return;
    }

    const formData = new FormData();
    Object.entries(postData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) formData.append(key, value);
    });
    formData.append("listing_title", postData.title);
    formData.append("sections", JSON.stringify(serializeSections()));

    try {
      const endpoint = isEdit ? `${ApiEndpoints.comparison.Update}${comparisonId}` : ApiEndpoints.comparison.Create;
      const response = await ApiRequest("POST", endpoint, formData);
      toast.success(response.message);
      if (!response.error) navigate("/comparison-page");
    } catch (error) {
      console.error("Error submitting comparison page:", error);
    }
  };

  return (
    <>
      <AdminHeader pagename={pagename} />

      <div className="add-content-page comparison-form-page">
        <form id="comparisonForm" onSubmit={handleSubmit}>
          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Title<small>*</small></label></div>
                  <input name="title" value={postData.title || ""} onChange={handleChange} type="text" className="form-control" placeholder="Enter Title" />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Slug<small>*</small></label></div>
                  <input name="slug" value={postData.slug || ""} onChange={handleChange} type="text" className="form-control" placeholder="Enter Slug" />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Author<small>*</small></label></div>
                  <CustomSelect defaultValue={postData.author_id} onChange={handleChange} name="author_id" options={authors} placeholder="Select Author" />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Date</label></div>
                  <input name="date" value={postData.date || ""} onChange={handleChange} type="date" className="form-control" />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Status</label></div>
                  <select name="status" value={postData.status || "0"} onChange={handleChange} className="form-control">
                    <option value="0">Enabled</option>
                    <option value="1">Disabled</option>
                  </select>
                </div>
              </div>
              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Listing Description<small>*</small></label></div>
                  <input name="listing_description" value={postData.listing_description || ""} onChange={handleChange} type="text" className="form-control" placeholder="Text shown on comparison listing cards" />
                </div>
              </div>
              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Detail Short Description<small>*</small></label></div>
                  <textarea name="short_description" value={postData.short_description || ""} onChange={handleChange} className="form-control comparison-textarea" rows="6" placeholder="Intro text under the detail page title" />
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-6">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add"><label>Listing Image (468*340px)<small>*</small></label></div>
                  <CustomImageUpload name="listing_image" acceptedFormats={[".webp"]} onImageChange={handleChange} imagePreview={previews.listing_image} />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add"><label>Banner Image (1608*684px)<small>*</small></label></div>
                  <CustomImageUpload name="hero_image" acceptedFormats={[".webp"]} onImageChange={handleChange} imagePreview={previews.hero_image} />
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">
              {sections.map((section, index) => (
                <div key={index} className="col-lg-12">
                  <div className="add-page-inputs">
                    <div className="label-wrap-add mb-2 d-flex justify-content-between align-items-center">
                      <label>Section {index + 1}{index === 0 && <small>*</small>}</label>
                      {sections.length > 1 && <button type="button" className="remove-btn" onClick={() => setSections((prev) => prev.filter((_, itemIndex) => itemIndex !== index))}>Remove</button>}
                    </div>
                    <input value={section.heading || ""} onChange={(event) => updateSection(index, "heading", event.target.value)} type="text" className="form-control" placeholder="Enter Section Heading" />
                  </div>
                  <div className="add-page-inputs">
                    <div className="label-wrap-add"><label>Section Description{index === 0 && <small>*</small>}</label></div>
                    <DynamicCKEditor data={section.description || ""} onChange={(event, editor) => updateSection(index, "description", editor.getData())} />
                  </div>
                  <div className="add-page-inputs">
                    <label className="d-flex align-items-center gap-2">
                      <input
                        type="checkbox"
                        checked={Boolean(section.comparison_table_enabled)}
                        onChange={(event) => updateSection(index, "comparison_table_enabled", event.target.checked)}
                      />
                      Comparison Table
                    </label>
                  </div>
                  {section.comparison_table_enabled && (
                    <div className="row">
                      {(section.comparison_table?.columns || emptyTable.columns).map((column, columnIndex) => (
                        <div key={columnIndex} className="col-lg-4">
                          <div className="add-page-inputs">
                            <div className="label-wrap-add"><label>Table Heading {columnIndex + 1}</label></div>
                            <input
                              value={column || ""}
                              onChange={(event) => updateSectionTableColumn(index, columnIndex, event.target.value)}
                              type="text"
                              className="form-control"
                              placeholder={`Enter Heading ${columnIndex + 1}`}
                            />
                          </div>
                        </div>
                      ))}

                      {(section.comparison_table?.rows || createEmptyTable().rows).map((row, rowIndex) => (
                        <div key={rowIndex} className="col-lg-12">
                          <div className="row align-items-end">
                            <div className="col-lg-4">
                              <div className="add-page-inputs">
                                <div className="label-wrap-add"><label>Feature</label></div>
                                <input
                                  value={row.feature || ""}
                                  onChange={(event) => updateSectionTableRow(index, rowIndex, "feature", event.target.value)}
                                  type="text"
                                  className="form-control"
                                  placeholder="Enter Feature"
                                />
                              </div>
                            </div>
                            {[0, 1].map((valueIndex) => (
                              <div key={valueIndex} className="col-lg-4">
                                <div className="add-page-inputs">
                                  <div className="label-wrap-add"><label>{valueIndex === 0 ? "Second Column" : "Third Column"}</label></div>
                                  <select
                                    value={row.values?.[valueIndex]?.type || "Yes"}
                                    onChange={(event) => updateSectionTableRowValue(index, rowIndex, valueIndex, "type", event.target.value)}
                                    className="form-control"
                                  >
                                    <option value="Yes">Yes</option>
                                    <option value="No">No</option>
                                    <option value="Content">Content</option>
                                  </select>
                                </div>
                                {row.values?.[valueIndex]?.type === "Content" && (
                                  <div className="add-page-inputs">
                                    <input
                                      value={row.values?.[valueIndex]?.content || ""}
                                      onChange={(event) => updateSectionTableRowValue(index, rowIndex, valueIndex, "content", event.target.value)}
                                      type="text"
                                      className="form-control"
                                      placeholder="Enter Content"
                                    />
                                  </div>
                                )}
                              </div>
                            ))}
                            <div className="col-lg-12">
                              {section.comparison_table?.rows?.length > 1 && (
                                <button type="button" className="remove-btn" onClick={() => removeSectionTableRow(index, rowIndex)}>Remove Row</button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}

                      <div className="col-lg-12">
                        <div className="add-page-inputs">
                          <Link className="addmore-images-client" onClick={(event) => { event.preventDefault(); addSectionTableRow(index); }}>Add More +</Link>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <Link className="addmore-images-client" onClick={(event) => { event.preventDefault(); setSections((prev) => [...prev, createEmptySection()]); }}>Add Section +</Link>
                </div>
              </div>
            </div>
          </div>
        </form>

        <button type="submit" form="comparisonForm" className="default-btn">publish <span></span></button>
      </div>
    </>
  );
}
